import { useState, useEffect } from "react";
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { getCrmDashboard } from "../services/api";

const ASAMA_RENK = {
  Lead: "#60a5fa", Iletisimde: "#a78bfa", Teklif: "#fbbf24",
  Muzakere: "#fb923c", Kazanildi: "#34d399", Kaybedildi: "#f87171",
};

const fmt = (n) => n == null ? "-" : Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2 }) + " TL";

const card = { background: "var(--bg-card-alt)", border: "1px solid var(--accent-bg)", borderRadius: 10, padding: 20 };
const th   = { padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: "var(--text-muted)", borderBottom: "1px solid var(--border-hairline)", textTransform: "uppercase", letterSpacing: ".05em" };
const td   = { padding: "11px 14px", fontSize: 13, color: "var(--text-primary)", borderBottom: "1px solid var(--border-hairline)" };

export default function CrmDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCrmDashboard()
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ color: "var(--text-faint)", fontSize: 13, padding: 32, textAlign: "center" }}>Yukleniyor...</div>;
  }
  if (!data) {
    return <div style={{ color: "var(--text-faint)", fontSize: 13, padding: 32, textAlign: "center" }}>Veri yuklenemedi.</div>;
  }

  const acikToplam = data.asamaOzeti.reduce((s, a) => s + (a.ToplamTutar || 0), 0);
  const kazanilan = data.buAyKapanan.find((a) => a.Asama === "Kazanildi");
  const kaybedilen = data.buAyKapanan.find((a) => a.Asama === "Kaybedildi");

  const barData = data.asamaOzeti.map((a) => ({
    name: a.Asama,
    Tutar: Number(a.ToplamTutar || 0),
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
        <div style={{ ...card, padding: 16 }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>Acik Pipeline Toplami</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginTop: 6 }}>{fmt(acikToplam)}</div>
        </div>
        <div style={{ ...card, padding: 16, borderColor: "#0d3b2e" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>Bu Ay Kazanilan</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#34d399", marginTop: 6 }}>
            {kazanilan ? `${kazanilan.Adet} adet · ${fmt(kazanilan.ToplamTutar)}` : "0 adet"}
          </div>
        </div>
        <div style={{ ...card, padding: 16, borderColor: "#431407" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>Bu Ay Kaybedilen</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#f87171", marginTop: 6 }}>
            {kaybedilen ? `${kaybedilen.Adet} adet · ${fmt(kaybedilen.ToplamTutar)}` : "0 adet"}
          </div>
        </div>
      </div>

      <div style={card}>
        <h3 style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 16, fontWeight: 600 }}>Asamaya Gore Acik Pipeline</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={barData} margin={{ top: 0, right: 20, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fill: "var(--text-secondary)", fontSize: 12 }} />
            <YAxis tick={{ fill: "var(--text-secondary)", fontSize: 12 }} />
            <Tooltip
              contentStyle={{ background: "var(--bg-page)", border: "1px solid var(--border)", borderRadius: 8 }}
              labelStyle={{ color: "var(--text-primary)" }}
              formatter={(value) => fmt(value)}
            />
            <Bar dataKey="Tutar" radius={[4, 4, 0, 0]}>
              {barData.map((entry, i) => (
                <Cell key={i} fill={ASAMA_RENK[entry.name] || "var(--accent)"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 600, color: "var(--accent-text)", borderBottom: "1px solid var(--border-hairline)" }}>
            Yaklasan Aktiviteler
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {data.yaklasanAktiviteler.length === 0 && (
                <tr><td style={{ ...td, textAlign: "center", color: "var(--text-faint)", padding: 24 }}>Yaklasan aktivite yok.</td></tr>
              )}
              {data.yaklasanAktiviteler.map((a) => (
                <tr key={a.ID}>
                  <td style={{ ...td, fontWeight: 600 }}>{a.Baslik}</td>
                  <td style={{ ...td, color: "var(--text-secondary)" }}>{a.FirmaUnvani || "-"}</td>
                  <td style={{ ...td, color: "var(--text-secondary)" }}>{String(a.Tarih).slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", fontSize: 13, fontWeight: 600, color: "var(--accent-text)", borderBottom: "1px solid var(--border-hairline)" }}>
            Acik Tutara Gore En Buyuk Firmalar
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {data.enBuyukFirmalar.length === 0 && (
                <tr><td style={{ ...td, textAlign: "center", color: "var(--text-faint)", padding: 24 }}>Kayit yok.</td></tr>
              )}
              {data.enBuyukFirmalar.map((f, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 600 }}>{f.FirmaUnvani}</td>
                  <td style={{ ...td, textAlign: "right", color: "#34d399", fontWeight: 600 }}>{fmt(f.AcikTutar)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
